// Canonical media-authorization status resolver.
//
// PARITY: this file must stay byte-identical between client (Cortex OS) and
// superadmin (Quantum OS), and behaviourally identical to the backend mirror at
// backend/utils/mediaAuthorizationStatus.js.
//
// Why this exists
// ---------------
// `contract_status` on a media-authorization record is the RAW DocuSign envelope
// transport state (sent / delivered / completed / voided / declined). DocuSign
// keeps an envelope at "delivered" until EVERY routed recipient finishes, so a
// contract the client has already signed still reports "delivered" while it waits
// on the Cphere countersignature. Rendering that transport value as the primary
// Authorization status makes a completed client authorization look unsigned.
//
// This resolver derives the GOVERNED authorization outcome from the canonical
// record. It never rewrites the raw envelope state — the raw value is preserved
// and returned alongside the governed status for diagnostics.
//
// Precedence is fixed by the canonical lifecycle, NOT by whichever sync or
// webhook value arrived most recently:
//
//   1. Voided envelope                     -> VOIDED
//   2. Declined signature                   -> DECLINED
//   3. Expired envelope                     -> EXPIRED
//   4. Fully executed (countersigned)       -> COMPLETED
//   5. Required client signature complete   -> AUTHORIZED
//   6. Delivered but unsigned               -> DELIVERED
//   7. Sent but not delivered               -> SENT
//   8. Anything else                        -> governed status passthrough
//                                              (DRAFT / UNDER REVIEW / APPROVED /
//                                               REPORTING ONLY / READY_TO_RESEND / …)
//
// Because step 5 is driven by persisted signature evidence rather than the latest
// transport value, a repeated "delivered" sync can never downgrade a record that
// has already reached AUTHORIZED.
//
// Steps 1-4 and 7 keep the record's own status verbatim when it already expresses
// that outcome (REFUSED, CANCELED, EXECUTED, AWAITING_CLIENT and friends are
// established governed states with their own pills). The canonical label is only
// substituted when the outcome had to be derived from evidence the raw status
// does not carry.

export const MEDIA_AUTHORIZATION_STATUS = Object.freeze({
  AUTHORIZED: "AUTHORIZED",
  COMPLETED: "COMPLETED",
  DELIVERED: "DELIVERED",
  SENT: "SENT",
  VOIDED: "VOIDED",
  DECLINED: "DECLINED",
  EXPIRED: "EXPIRED",
  PENDING: "PENDING",
});

export const MEDIA_AUTHORIZATION_STATUS_REASON = Object.freeze({
  ENVELOPE_VOIDED: "ENVELOPE_VOIDED",
  SIGNATURE_DECLINED: "SIGNATURE_DECLINED",
  ENVELOPE_EXPIRED: "ENVELOPE_EXPIRED",
  FULLY_EXECUTED: "FULLY_EXECUTED",
  CLIENT_SIGNATURE_COMPLETE: "CLIENT_SIGNATURE_COMPLETE",
  ENVELOPE_DELIVERED: "ENVELOPE_DELIVERED",
  ENVELOPE_SENT: "ENVELOPE_SENT",
  GOVERNED_STATUS_PASSTHROUGH: "GOVERNED_STATUS_PASSTHROUGH",
});

const VOID_TOKENS = new Set(["VOIDED", "VOID", "CANCELED", "CANCELLED"]);
const DECLINED_TOKENS = new Set(["DECLINED", "REFUSED", "REJECTED"]);
const EXPIRED_TOKENS = new Set(["EXPIRED"]);
const COMPLETED_TOKENS = new Set(["COMPLETED", "EXECUTED", "COUNTERSIGNED"]);
const CLIENT_SIGNED_TOKENS = new Set(["CLIENT_SIGNED", "AWAITING_COUNTERSIGN"]);
const DELIVERED_TOKENS = new Set(["DELIVERED"]);
const SENT_TOKENS = new Set(["SENT", "AWAITING_CLIENT"]);

// DocuSign recipient statuses that mean "this recipient finished signing".
const RECIPIENT_SIGNED_TOKENS = new Set(["COMPLETED", "SIGNED"]);
const RECIPIENT_DECLINED_TOKENS = new Set(["DECLINED"]);

const normalizeToken = (value) =>
  String(value ?? "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");

const parseStrategyContract = (campaign) => {
  const explicit =
    campaign?.strategy_payload_contract ?? campaign?.strategyPayloadContract ?? null;
  if (explicit) {
    if (typeof explicit === "string") {
      try {
        return JSON.parse(explicit) || null;
      } catch {
        return null;
      }
    }
    if (typeof explicit === "object") return explicit;
  }

  const payload = campaign?.strategy_payload ?? campaign?.strategyPayload ?? null;
  if (!payload) return null;

  if (typeof payload === "string") {
    try {
      const parsed = JSON.parse(payload);
      return parsed?.contract || null;
    } catch {
      return null;
    }
  }

  return payload?.contract || null;
};

// Flat campaign columns win over the nested contract blob, matching the page
// accessors these pages have always used.
export const getMediaAuthorizationSource = (campaign) => {
  const nested = parseStrategyContract(campaign) || {};
  const flat = campaign && typeof campaign === "object" ? campaign : {};
  return { ...nested, ...flat };
};

// The raw governed/transport status exactly as the record carries it. This is the
// value the pages used to render directly, and it stays available unchanged.
export const getRawAuthorizationStatus = (campaign) => {
  const src = getMediaAuthorizationSource(campaign);
  const raw =
    src.contract_status ??
    src.contractStatus ??
    src.authorization_status ??
    src.authorizationStatus ??
    src.status ??
    src.approval_status ??
    "NOT_STARTED";

  const normalized = String(raw).toUpperCase();
  return normalized === "NOT_STARTED" ? MEDIA_AUTHORIZATION_STATUS.PENDING : normalized;
};

// The raw DocuSign envelope status, when the record records it separately from
// the governed contract status. Never falsified, never overwritten.
export const getRawEnvelopeStatus = (campaign) => {
  const src = getMediaAuthorizationSource(campaign);
  const raw =
    src.docusign_envelope_status ??
    src.docusignEnvelopeStatus ??
    src.envelope_status ??
    src.envelopeStatus ??
    null;
  const normalized = String(raw ?? "").trim();
  return normalized || null;
};

const getEnvelopeId = (campaign) => {
  const src = getMediaAuthorizationSource(campaign);
  return (
    src.envelope_id ??
    src.envelopeId ??
    src.docusign_envelope_id ??
    src.docusignEnvelopeId ??
    null
  );
};

const getVoidedEnvelopeId = (campaign) => {
  const src = getMediaAuthorizationSource(campaign);
  return (
    src.voided_envelope_id ??
    src.voidedEnvelopeId ??
    src.last_voided_envelope_id ??
    src.lastVoidedEnvelopeId ??
    null
  );
};

const getClientSignedAt = (campaign) => {
  const src = getMediaAuthorizationSource(campaign);
  return src.client_signed_at ?? src.clientSignedAt ?? src.signed_at ?? src.signedAt ?? null;
};

const getCountersignedAt = (campaign) => {
  const src = getMediaAuthorizationSource(campaign);
  return (
    src.countersigned_at ?? src.counterSignedAt ?? src.countersignedAt ?? src.countersign_at ?? null
  );
};

const getClientRecipientStatus = (campaign) => {
  const src = getMediaAuthorizationSource(campaign);
  return normalizeToken(
    src.client_recipient_status ??
      src.clientRecipientStatus ??
      src.client_status ??
      src.clientStatus ??
      ""
  );
};

const getCountersignerRecipientStatus = (campaign) => {
  const src = getMediaAuthorizationSource(campaign);
  return normalizeToken(
    src.countersigner_recipient_status ??
      src.countersignerRecipientStatus ??
      src.countersigner_status ??
      src.countersignerStatus ??
      ""
  );
};

// Signature timestamps belong to a specific envelope. After a void-and-reset the
// envelope fields are cleared and the record returns to READY_TO_RESEND, so the
// old timestamps must stop counting as authorization evidence.
const isSignatureEvidenceCurrent = (campaign) => {
  const envelopeId = getEnvelopeId(campaign);
  if (!envelopeId) return false;

  const voidedEnvelopeId = getVoidedEnvelopeId(campaign);
  if (voidedEnvelopeId && String(voidedEnvelopeId) === String(envelopeId)) return false;

  return true;
};

const hasClientSignature = (campaign) => {
  const statusToken = normalizeToken(getRawAuthorizationStatus(campaign));
  if (CLIENT_SIGNED_TOKENS.has(statusToken)) return true;

  if (!isSignatureEvidenceCurrent(campaign)) return false;
  if (getClientSignedAt(campaign)) return true;

  return RECIPIENT_SIGNED_TOKENS.has(getClientRecipientStatus(campaign));
};

const hasCountersignature = (campaign) => {
  if (!isSignatureEvidenceCurrent(campaign)) return false;
  if (getCountersignedAt(campaign)) return true;
  return RECIPIENT_SIGNED_TOKENS.has(getCountersignerRecipientStatus(campaign));
};

const hasDeclineEvidence = (campaign, tokens) => {
  if (tokens.some((token) => DECLINED_TOKENS.has(token))) return true;
  if (!isSignatureEvidenceCurrent(campaign)) return false;
  return (
    RECIPIENT_DECLINED_TOKENS.has(getClientRecipientStatus(campaign)) ||
    RECIPIENT_DECLINED_TOKENS.has(getCountersignerRecipientStatus(campaign))
  );
};

/**
 * Resolve the governed, user-facing authorization status for a canonical record.
 *
 * @returns {{
 *   status: string,          // the governed status to display
 *   reason: string,          // why this status won (diagnostics)
 *   rawStatus: string,       // the record's raw governed/transport status, unchanged
 *   rawEnvelopeStatus: ?string, // the raw DocuSign envelope status, unchanged
 *   clientSignatureComplete: boolean,
 *   countersignatureComplete: boolean
 * }}
 */
export const resolveMediaAuthorizationStatus = (campaign) => {
  const rawStatus = getRawAuthorizationStatus(campaign);
  const rawEnvelopeStatus = getRawEnvelopeStatus(campaign);

  const statusToken = normalizeToken(rawStatus);
  // The recorded envelope status belongs to a specific envelope. After a
  // void-and-reset the envelope is cleared but the last transport value can
  // linger, so it stops being derivation evidence exactly like the signature
  // timestamps do. It is still reported verbatim as rawEnvelopeStatus.
  const envelopeToken = isSignatureEvidenceCurrent(campaign)
    ? normalizeToken(rawEnvelopeStatus || "")
    : "";
  const tokens = envelopeToken ? [statusToken, envelopeToken] : [statusToken];

  const clientSignatureComplete = hasClientSignature(campaign);
  const countersignatureComplete = hasCountersignature(campaign);

  const build = (status, reason) =>
    Object.freeze({
      status,
      reason,
      rawStatus,
      rawEnvelopeStatus,
      clientSignatureComplete,
      countersignatureComplete,
    });

  // When the record's own status already expresses the governed outcome, keep it
  // verbatim — REFUSED, CANCELED and EXECUTED are established governed states
  // with their own pills, and collapsing them would restate unrelated records.
  // The canonical label is only substituted when the outcome was derived from
  // evidence the raw status does not carry.
  const buildOutcome = (canonical, family, reason) =>
    build(family.has(statusToken) ? rawStatus : canonical, reason);

  if (tokens.some((token) => VOID_TOKENS.has(token))) {
    return buildOutcome(
      MEDIA_AUTHORIZATION_STATUS.VOIDED,
      VOID_TOKENS,
      MEDIA_AUTHORIZATION_STATUS_REASON.ENVELOPE_VOIDED
    );
  }

  if (hasDeclineEvidence(campaign, tokens)) {
    return buildOutcome(
      MEDIA_AUTHORIZATION_STATUS.DECLINED,
      DECLINED_TOKENS,
      MEDIA_AUTHORIZATION_STATUS_REASON.SIGNATURE_DECLINED
    );
  }

  if (tokens.some((token) => EXPIRED_TOKENS.has(token))) {
    return buildOutcome(
      MEDIA_AUTHORIZATION_STATUS.EXPIRED,
      EXPIRED_TOKENS,
      MEDIA_AUTHORIZATION_STATUS_REASON.ENVELOPE_EXPIRED
    );
  }

  if (countersignatureComplete || tokens.some((token) => COMPLETED_TOKENS.has(token))) {
    return buildOutcome(
      MEDIA_AUTHORIZATION_STATUS.COMPLETED,
      COMPLETED_TOKENS,
      MEDIA_AUTHORIZATION_STATUS_REASON.FULLY_EXECUTED
    );
  }

  if (clientSignatureComplete) {
    return build(
      MEDIA_AUTHORIZATION_STATUS.AUTHORIZED,
      MEDIA_AUTHORIZATION_STATUS_REASON.CLIENT_SIGNATURE_COMPLETE
    );
  }

  if (tokens.some((token) => DELIVERED_TOKENS.has(token))) {
    return build(
      MEDIA_AUTHORIZATION_STATUS.DELIVERED,
      MEDIA_AUTHORIZATION_STATUS_REASON.ENVELOPE_DELIVERED
    );
  }

  if (tokens.some((token) => SENT_TOKENS.has(token))) {
    return buildOutcome(
      MEDIA_AUTHORIZATION_STATUS.SENT,
      SENT_TOKENS,
      MEDIA_AUTHORIZATION_STATUS_REASON.ENVELOPE_SENT
    );
  }

  return build(rawStatus, MEDIA_AUTHORIZATION_STATUS_REASON.GOVERNED_STATUS_PASSTHROUGH);
};

// Convenience: the single governed status string. Every surface that shows a
// primary Authorization status must read it from here.
export const getGovernedAuthorizationStatus = (campaign) =>
  resolveMediaAuthorizationStatus(campaign).status;

// True for every record whose governed outcome is "fully executed", whichever
// label it carries (COMPLETED / EXECUTED / COUNTERSIGNED, or a countersignature
// recorded while the envelope still reads delivered). Consume this rather than
// comparing against a status string.
export const isFullyExecutedAuthorization = (campaign) =>
  resolveMediaAuthorizationStatus(campaign).reason ===
  MEDIA_AUTHORIZATION_STATUS_REASON.FULLY_EXECUTED;
